<template>
  <div ref="mapContainer" class="hotels-map"></div>
</template>
<script setup>
import { storeToRefs } from 'pinia'
import { onMounted, ref, watch } from 'vue'

import { initModal } from '../../../../../blocks/hotel_list/hotel_card_info_modal/hotel_card_info_modal'
import { getData } from '../../../../../core/iberapi/hotels_data/hotels_data'
import { currentDevice } from '../../../../../core/utils/currentDevice'
import fastbooking from '../../../../../shared/fastbooking/ibh_fastbooking'
import { addMarkers, init as initMap, removeMarkers } from '../../../../../shared/map/map'
import createHotel from '../../../../../shared/map/map.hotels'
import Modal from '../../../../../shared/modal/modal'
import { useHotelsInfoStore } from '../../../blocks/HotelsInfo/store/hotels_info'
import { loadStyles } from '../../../mixins/LoadStyles/LoadStyles'
import { emitUtagEvent } from '../HotelsInfo.analytics'

/**
 * Styles
 */
const COMPONENT_NAME = 'HotelsMap'
loadStyles({ name: COMPONENT_NAME, folder: 'components/HotelsInfo' })

/**
 * Refs
 */
const mapContainer = ref(null)
let map = null

/**
 * Store
 */
const hotelsInfoStore = useHotelsInfoStore()
const { hotelsFiltered: hotels } = storeToRefs(hotelsInfoStore)

/**
 * Methods
 */
function getExtraData(hotelAbrev) {
  let priceReal, priceHtml, hrefHotelValue, cardTextHotelValue
  const hotelsValue = hotels.value

  hotelsValue.forEach(function (hotel) {
    if (hotel.abrev === hotelAbrev) {
      hrefHotelValue = hotel.fastbooking
      priceReal = hotel.price_real
      priceHtml = priceReal.price_with_coin
      cardTextHotelValue = hotel.card_text
    }
  })

  return { priceReal, priceHtml, hrefHotelValue, cardTextHotelValue }
}

const infoModalContent = element => {
  const extraInfo = element.querySelector('.js-extra-info')

  const priceReal = {
    price_with_coin: extraInfo.querySelector('.js-price-coin').innerHTML,
    base_price_with_coin: extraInfo.querySelector('.js-base-price-coin').innerHTML,
    price_value: element.dataset.price,
    currency: element.dataset.currency,
    base_price_value: element.dataset.basePrice,
    resident_price_value: element.dataset.residentPrice,
    resident_type: element.dataset.residentType,
    promocode: element.dataset.promocode,
  }

  const infoCardForModal = {
    hotelPrice: priceReal,
    hotelCardText: element.dataset.cardText,
    hotelRealPrice: extraInfo.querySelector('.js-price').innerHTML,
    linkUrl: element.dataset.bookingUrl,
  }

  return infoCardForModal
}

function handleCTAClick(map) {
  const mapContianer = map.mapContainer
  mapContianer.addEventListener('click', function (e) {
    IB.currencyForm.forceUpdate()
    const target = e.target
    if (target.closest('.js-open-modal')) {
      e.preventDefault()

      const openModalButton = target.closest('.js-open-modal')
      if (IB.currentPageType.get() !== 'bookings') {
        if (!currentDevice.isDesktop) {
          Modal.close()
        }

        emitUtagEvent({
          event_name: 'hotel_services',
          event_cat: 'hotel information',
          event_act: 'see hotel info',
          event_lbl: '',
          event_purpose: 'inspiration',
          event_structure: 'body',
        })
        initModal({
          element: openModalButton,
          hotelId: openModalButton.dataset.hotelId,
          infoModalContent: infoModalContent(openModalButton),
          contentToShow: 'information',
        })
      }
    }
    const ctaButton = target.closest('.js-book-btn')

    if (ctaButton && IB.currentPageType.get() !== 'bookings') {
      e.preventDefault()

      const hotelId = target.dataset.hotelId
      fastbooking.setDestination(hotelId)

      if (!currentDevice.isDesktop) {
        fastbooking.setFastbookingSticky()
        fastbooking.openStickyFastbooking()
        Modal.close()
      }

      fastbooking.openDates()
    }
  })
}

function getMapItems() {
  const hotelsData = getData()
  const hotelList = hotelsData.hotels[1]
  const hotelAbrevs = hotels.value.map(hotel => hotel.abrev)
  const hotelsFiltered = hotelList.filter(hotel => hotelAbrevs.includes(hotel.data.abrev))
  const items = []

  hotelsFiltered.forEach(function (hotel) {
    const hotelAbrev = hotel.data.abrev
    const extraData = getExtraData(hotelAbrev)
    const href = extraData.hrefHotelValue
    const priceReal = extraData.priceReal
    const priceHtml = extraData.priceHtml
    const cardText = extraData.cardTextHotelValue

    hotel.data.fastbookingPath = href
    hotel.data.cardText = cardText
    hotel.data.priceReal = priceReal
    hotel.data.priceHtml = priceHtml

    const item = createHotel(hotel)
    items.push(item)
  })

  return items
}

/**
 * Subscribe to the store to watch for hotels changes in order to update the map
 */
watch(hotels, () => {
  const items = getMapItems()
  if (map) {
    removeMarkers(map)
    addMarkers(items, map)
  }
})

/**
 * Lifecycle
 */
onMounted(async () => {
  const items = getMapItems()
  map = await initMap(mapContainer.value, items)
  handleCTAClick(map)
})
</script>
